import { Injectable, OnDestroy, NgZone } from '@angular/core';
import io from 'socket.io-client';
import { LocalService, WeightMachineConnetion } from '../local.service';

interface WeightSocketConnection {
  connection: WeightMachineConnetion;
  socket: any;
  key: string;
}

@Injectable({
  providedIn: 'root',
})
export class WsOfflineService implements OnDestroy {
  private activeSockets: WeightSocketConnection[] = [];
  private checkInterval: any = null;

  constructor(
    private localService: LocalService,
    private zone: NgZone,
  ) {
    // Check initial settings and start periodic check for connection updates
    this.checkWeightMachineWebSocketConnection();
    this.checkInterval = setInterval(() => {
      this.checkWeightMachineWebSocketConnection();
    }, 5000);
  }

  public checkWeightMachineWebSocketConnection() {
    const connections = this.localService.getAvailableConnectionForWeighMachine() || [];
    this.syncConnections(connections);
  }

  private syncConnections(connections: WeightMachineConnetion[]) {
    const newKeys = new Set<string>();

    connections.forEach((conn) => {
      // Prioritize directUrl if available; else fallback to networkUrl
      const targetUrl = conn.directUrl && conn.directUrl.trim() !== '' && conn.directUrl.trim().length > 5
        ? conn.directUrl.trim()
        : conn.networkUrl?.trim();

      if (!targetUrl) return;

      const key = `${conn.businessId || ''}_${targetUrl}_${conn.portPath || ''}`;
      newKeys.add(key);

      const existing = this.activeSockets.find((s) => s.key === key);
      if (!existing) {
        this.connectSocket(conn, targetUrl, key);
      }
    });

    // Remove sockets no longer present in connections list
    for (let i = this.activeSockets.length - 1; i >= 0; i--) {
      const active = this.activeSockets[i];
      if (!newKeys.has(active.key)) {
        this.disconnectSocket(active);
        this.activeSockets.splice(i, 1);
      }
    }
  }

  private connectSocket(conn: WeightMachineConnetion, targetUrl: string, key: string) {
    try {
      const isDirect = !!(conn.directUrl && conn.directUrl.trim() !== '' && conn.directUrl.trim().length > 5);

      const options: any = {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 2000,
        timeout: 5000,
      };

      // When using networkUrl, pass portPath as path query if provided
      if (!isDirect && conn.portPath) {
        options.query = { path: conn.portPath };
      }

      const socket = io(targetUrl, options);

      socket.on('connect', () => {
        if (!isDirect && conn.portPath) {
          socket.emit('subscribe-weight', { path: conn.portPath });
        }
      });

      socket.on('weight-data', (data: any) => {
        try {
          const selectedBizId = this.localService.getSelectedBusinessId();
          // Filter data by selected businessId if connection has a businessId
          if (conn.businessId && selectedBizId && conn.businessId !== selectedBizId) {
            return;
          }

          if (data && data.weight !== undefined && data.weight !== null) {
            const weightStr = data.weight.toString();
            const numericWeight = weightStr.replace(/[^0-9.]/g, '');
            this.zone.run(() => {
              if (numericWeight) {
                this.localService.billQty = parseFloat(numericWeight);
              } else {
                this.localService.billQty = null;
              }
            });
          }
        } catch (e) {
          // error parsing weight data
        }
      });

      socket.on('disconnect', (reason: string) => { });
      socket.on('connect_error', (error: any) => { });

      this.activeSockets.push({ connection: conn, socket, key });
    } catch (error) {
      // error establishing weight machine socket
    }
  }

  private disconnectSocket(active: WeightSocketConnection) {
    if (active && active.socket) {
      try {
        active.socket.disconnect();
      } catch (e) { }
    }
  }

  private disconnectAllSockets() {
    this.activeSockets.forEach((active) => this.disconnectSocket(active));
    this.activeSockets = [];
  }

  ngOnDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    this.disconnectAllSockets();
  }
}
